export const SITE_URL = 'https://autojoaopires.samworks.online';
export const SITE_NAME = 'Auto João Pires';
export const SITE_TAGLINE = 'Compra & Venda de Automóveis Semi-Novos & Usados';
export const INSTAGRAM_HANDLE = 'autojoaopirespt';
export const TWITTER_HANDLE = '@autojoaopirespt';

export const SOCIAL_LINKS = {
    instagram: 'https://www.instagram.com/autojoaopirespt/',
    whatsapp: 'https://ig.me/m/autojoaopirespt',
    instagramDm: 'https://ig.me/m/autojoaopirespt',
};

export const SHARE_IMAGE = '/hero-bg.webp';
export const SHARE_IMAGE_ALT =
    'Auto João Pires — stand de automóveis semi-novos e usados, mecânica e estética automóvel';

export const DEFAULT_SEO = {
    title: `${SITE_NAME} | ${SITE_TAGLINE}`,
    description:
        'Stand Auto João Pires: compra e venda de automóveis semi-novos e usados, mecânica ligeira, manutenção, inspeções, higienização, lavagem, polimentos e cerâmica. Contacte @autojoaopirespt.',
    keywords:
        'auto joão pires, autojoaopires, stand automóveis, carros usados portugal, semi-novos, compra venda automóveis, mecânica ligeira, manutenção automóvel, inspeções, lavagem auto, polimento carro, cerâmica automóvel, stand carros, @autojoaopirespt',
    image: SHARE_IMAGE,
    imageWidth: 1920,
    imageHeight: 1080,
    locale: 'pt_PT',
    phone: null,
    phoneDisplay: '@autojoaopirespt',
    email: null,
};

export const PAGE_SEO = {
    home: {
        title: DEFAULT_SEO.title,
        description: DEFAULT_SEO.description,
        path: '/',
    },
    stock: {
        title: 'Stock de Veículos Semi-Novos & Usados',
        description:
            'Explore o stock da Auto João Pires. Automóveis semi-novos e usados selecionados com transparência. Veja preços, fotos e detalhes de cada viatura.',
        path: '/automoveis',
        image: SHARE_IMAGE,
    },
    about: {
        title: 'Sobre Nós — Stand & Oficina',
        description:
            'Conheça a Auto João Pires: compra e venda de automóveis, mecânica ligeira, manutenção, inspeções e serviços de estética automóvel num só espaço.',
        path: '/sobre',
    },
    contact: {
        title: 'Contactos',
        description:
            'Fale connosco via Instagram @autojoaopirespt. Auto João Pires — compra, venda e serviços automóveis.',
        path: '/contactos',
    },
    privacy: {
        title: 'Privacidade & Cookies',
        description:
            'Política de Privacidade e Cookies da Auto João Pires. Saiba como protegemos os seus dados pessoais.',
        path: '/privacidade',
        noindex: true,
    },
    terms: {
        title: 'Termos & Condições',
        description:
            'Termos e Condições de utilização do website da Auto João Pires.',
        path: '/termos',
        noindex: true,
    },
};

const CANONICAL_PATHS = {
    '/sobre': ['/Sobre', '/sobre'],
    '/contactos': ['/Contactos', '/contactos', '/contacto'],
    '/privacidade': ['/privacidade', '/privacidade/'],
    '/termos': ['/termos', '/termos/'],
    '/automoveis': ['/Automoveis', '/automoveis', '/stock'],
};

export const normalizeCanonicalPath = (path = '/') => {
    if (!path || path === '/') return '/';

    const clean = path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';

    for (const [canonical, aliases] of Object.entries(CANONICAL_PATHS)) {
        if (aliases.some((alias) => alias.replace(/\/$/, '') === clean)) {
            return canonical;
        }
    }

    if (clean.startsWith('/automoveis/')) {
        return clean;
    }

    return clean.startsWith('/') ? clean : `/${clean}`;
};

export const getCanonicalUrl = (path = '/') => {
    const normalized = normalizeCanonicalPath(path);
    if (normalized === '/') {
        return SITE_URL;
    }
    return `${SITE_URL}${normalized}`;
};

export const getAbsoluteImageUrl = (imagePath = DEFAULT_SEO.image) => {
    if (!imagePath) {
        return getAbsoluteImageUrl(DEFAULT_SEO.image);
    }

    if (imagePath.startsWith('http')) {
        return imagePath;
    }

    return `${SITE_URL}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
};

export const buildPageTitle = (title) =>
    title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

export const buildBreadcrumbSchema = (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: getCanonicalUrl(item.path),
    })),
});

export const buildAutoDealerSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    name: SITE_NAME,
    alternateName: 'AJP',
    image: getAbsoluteImageUrl(SHARE_IMAGE),
    logo: getAbsoluteImageUrl('/logo.jpg'),
    description: DEFAULT_SEO.description,
    url: SITE_URL,
    priceRange: '€€',
    areaServed: {
        '@type': 'Country',
        name: 'Portugal',
    },
    sameAs: [SOCIAL_LINKS.instagram],
    contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        url: SOCIAL_LINKS.instagram,
        availableLanguage: ['Portuguese'],
    },
});

export const buildOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: 'AJP',
    url: SITE_URL,
    logo: getAbsoluteImageUrl('/logo.jpg'),
    image: getAbsoluteImageUrl(SHARE_IMAGE),
    description:
        'Compra e venda de automóveis semi-novos e usados, mecânica ligeira, manutenção, inspeções e serviços de estética automóvel.',
    sameAs: [SOCIAL_LINKS.instagram],
});

export const buildWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: 'Auto João Pires Stand',
    url: SITE_URL,
    inLanguage: 'pt-PT',
    description: DEFAULT_SEO.description,
    publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: getAbsoluteImageUrl('/logo.jpg'),
    },
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/automoveis?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
    },
});

export const buildAutoDetailingServiceSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Higienização, Lavagem, Polimentos & Cerâmica',
    description:
        'Higienização e lavagem automóvel, polimentos e aplicação de cerâmica com produtos de qualidade na Auto João Pires.',
    provider: {
        '@type': 'AutoDealer',
        name: SITE_NAME,
        url: SITE_URL,
    },
    areaServed: {
        '@type': 'Country',
        name: 'Portugal',
    },
    serviceType: 'Estética e detalhe automóvel',
    url: getCanonicalUrl('/'),
    sameAs: [SOCIAL_LINKS.instagram],
});

export const buildMechanicServiceSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Mecânica Ligeira — Manutenção & Inspeções',
    description:
        'Mecânica ligeira, manutenção preventiva e preparação para inspeções automóveis na Auto João Pires.',
    provider: {
        '@type': 'AutoDealer',
        name: SITE_NAME,
        url: SITE_URL,
    },
    areaServed: {
        '@type': 'Country',
        name: 'Portugal',
    },
    serviceType: 'Mecânica automóvel',
    url: getCanonicalUrl('/'),
});

export const buildFAQSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'Que tipo de automóveis vende a Auto João Pires?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'A Auto João Pires comercializa automóveis semi-novos e usados, selecionados com rigor e transparência.',
            },
        },
        {
            '@type': 'Question',
            name: 'A Auto João Pires faz manutenção e inspeções?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Sim. Oferecemos mecânica ligeira, manutenção e apoio na preparação para inspeções.',
            },
        },
        {
            '@type': 'Question',
            name: 'Fazem lavagem, polimento e cerâmica?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Sim. Realizamos higienização, lavagem automóvel, polimentos e aplicação de cerâmica.',
            },
        },
        {
            '@type': 'Question',
            name: 'Como posso contactar a Auto João Pires?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Pode contactar-nos via Instagram em @autojoaopirespt ou através do formulário de contactos no website.',
            },
        },
    ],
});

export const buildContactPageSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: `Contactos | ${SITE_NAME}`,
    url: getCanonicalUrl('/contactos'),
    description: PAGE_SEO.contact.description,
    mainEntity: {
        '@type': 'AutoDealer',
        name: SITE_NAME,
        url: SITE_URL,
        sameAs: [SOCIAL_LINKS.instagram],
    },
});

export const buildAboutPageSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `Sobre Nós | ${SITE_NAME}`,
    url: getCanonicalUrl('/sobre'),
    description: PAGE_SEO.about.description,
    mainEntity: buildOrganizationSchema(),
});

export const buildItemListSchema = (cars = []) => {
    const available = cars
        .filter((car) => Number(car.vendido) !== 1)
        .slice(0, 30);

    if (available.length === 0) return null;

    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `Stock de Veículos | ${SITE_NAME}`,
        url: getCanonicalUrl('/automoveis'),
        numberOfItems: available.length,
        itemListElement: available.map((car, index) => {
            const carId = car.id || car.instagramShortcode || `viatura-${index + 1}`;
            return {
                '@type': 'ListItem',
                position: index + 1,
                name: car.titulo || car.marca,
                url: getCanonicalUrl(`/automoveis/${encodeURIComponent(carId)}`),
                image: car.thumb_url || getAbsoluteImageUrl(SHARE_IMAGE),
            };
        }),
    };
};

const parsePrice = (preco) => {
    if (!preco) return undefined;
    const cleaned = String(preco).replace(/[^\d.,]/g, '');
    const normalized =
        cleaned.includes('.') && cleaned.includes(',')
            ? cleaned.replace(/\./g, '').replace(',', '.')
            : cleaned.replace(/\./g, '').replace(',', '.');
    const value = parseFloat(normalized);
    return Number.isFinite(value) ? value : undefined;
};

export const buildCarProductSchema = (car, id) => {
    if (!car) return null;

    const price = parsePrice(car.preco);

    return {
        '@context': 'https://schema.org',
        '@type': 'Car',
        name: car.titulo,
        description: `${car.marca} ${car.titulo}${car.ano && car.ano !== '—' ? ` (${car.ano})` : ''} disponível na ${SITE_NAME}.`,
        image: car.thumb_url || getAbsoluteImageUrl(SHARE_IMAGE),
        brand: {
            '@type': 'Brand',
            name: car.marca || SITE_NAME,
        },
        ...(car.ano && car.ano !== '—' ? { vehicleModelDate: String(car.ano) } : {}),
        ...(price
            ? {
                  offers: {
                      '@type': 'Offer',
                      priceCurrency: 'EUR',
                      price,
                      availability:
                          Number(car.vendido) === 1
                              ? 'https://schema.org/SoldOut'
                              : 'https://schema.org/InStock',
                      url: getCanonicalUrl(`/automoveis/${id}`),
                      seller: {
                          '@type': 'AutoDealer',
                          name: SITE_NAME,
                          url: SITE_URL,
                      },
                  },
              }
            : {}),
        url: getCanonicalUrl(`/automoveis/${id}`),
    };
};

export const getHomeStructuredData = () => [
    buildAutoDealerSchema(),
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildAutoDetailingServiceSchema(),
    buildMechanicServiceSchema(),
    buildFAQSchema(),
];
